/**
 * Argo OS — storage adapter.
 *
 * Exposes a single `db` that satisfies the `Store` interface. The
 * concrete implementation is chosen at module load: the Firestore
 * adapter when `useFirestore` is true (real Firebase config + opt-in
 * flag), otherwise the localStorage adapter in `store-local.ts` — which
 * is what lets the app run in local dev with no Firebase config.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { firestore, useFirestore } from '../firebase';
import type { CollectionName, CollectionTypes } from './types';
import { localDb } from './store-local';
import { emitStoreHealth } from './storeHealth';

// --- Public API ------------------------------------------------------------

export interface Store {
  list<C extends CollectionName>(collection: C): Promise<CollectionTypes[C][]>;
  get<C extends CollectionName>(
    collection: C,
    id: string,
  ): Promise<CollectionTypes[C] | null>;
  /** Insert. Throws if a record with the same id already exists. */
  add<C extends CollectionName>(
    collection: C,
    record: CollectionTypes[C],
  ): Promise<void>;
  /** Upsert. Replaces by id if present, inserts otherwise. */
  put<C extends CollectionName>(
    collection: C,
    record: CollectionTypes[C],
  ): Promise<void>;
  /** Patch a record by id. Throws if it doesn't exist. */
  update<C extends CollectionName>(
    collection: C,
    id: string,
    patch: Partial<CollectionTypes[C]>,
  ): Promise<CollectionTypes[C]>;
  remove<C extends CollectionName>(collection: C, id: string): Promise<void>;
  /** Subscribe to changes; returns an unsubscribe function. */
  subscribe<C extends CollectionName>(
    collection: C,
    fn: (records: CollectionTypes[C][]) => void,
  ): () => void;
  /** Replace the entire contents of a collection. Used by the seeder. */
  replaceAll<C extends CollectionName>(
    collection: C,
    records: CollectionTypes[C][],
  ): Promise<void>;
}

// --- Implementation -------------------------------------------------------

function fdb() {
  if (!firestore) {
    throw new Error(
      '[store] Firestore is not initialized. ' +
        'Check VITE_FIREBASE_* env vars.',
    );
  }
  return firestore;
}

/** Strip Firestore metadata back into a plain Argo record. */
function hydrate<T>(data: DocumentData, id: string): T {
  return { ...(data as object), id } as T;
}

const firestoreDb: Store = {
  async list<C extends CollectionName>(name: C) {
    const snap = await getDocs(collection(fdb(), name));
    return snap.docs.map((d) => hydrate<CollectionTypes[C]>(d.data(), d.id));
  },

  async get<C extends CollectionName>(name: C, id: string) {
    const ref = doc(fdb(), name, id);
    const d = await getDoc(ref);
    return d.exists() ? hydrate<CollectionTypes[C]>(d.data(), d.id) : null;
  },

  async add<C extends CollectionName>(name: C, record: CollectionTypes[C]) {
    const id = (record as { id: string }).id;
    await setDoc(doc(fdb(), name, id), record as DocumentData);
  },

  async put<C extends CollectionName>(name: C, record: CollectionTypes[C]) {
    const id = (record as { id: string }).id;
    await setDoc(doc(fdb(), name, id), record as DocumentData);
  },

  async update<C extends CollectionName>(
    name: C,
    id: string,
    patch: Partial<CollectionTypes[C]>,
  ) {
    await updateDoc(doc(fdb(), name, id), patch as DocumentData);
    const after = await getDoc(doc(fdb(), name, id));
    if (!after.exists()) {
      throw new Error(`[store] update target ${name}/${id} vanished`);
    }
    return hydrate<CollectionTypes[C]>(after.data(), after.id);
  },

  async remove<C extends CollectionName>(name: C, id: string) {
    await deleteDoc(doc(fdb(), name, id));
  },

  subscribe<C extends CollectionName>(
    name: C,
    fn: (records: CollectionTypes[C][]) => void,
  ) {
    const unsub = onSnapshot(
      collection(fdb(), name),
      (snap) => {
        fn(snap.docs.map((d) => hydrate<CollectionTypes[C]>(d.data(), d.id)));
      },
      // Previously absent: Firestore permission/network errors used to
      // fail silently here. Now they're logged + surfaced as a toast so
      // the operator knows live sync dropped.
      (error) => {
        console.error(`[store] subscription to "${name}" failed`, error);
        emitStoreHealth({
          level: 'error',
          scope: name,
          message: `Live sync for ${name} was interrupted.`,
          detail: error.message,
        });
      },
    );
    return unsub;
  },

  async replaceAll<C extends CollectionName>(
    name: C,
    records: CollectionTypes[C][],
  ) {
    const batch = writeBatch(fdb());
    const existing = await getDocs(collection(fdb(), name));
    existing.forEach((d) => batch.delete(d.ref));
    records.forEach((r) =>
      batch.set(doc(fdb(), name, (r as { id: string }).id), r as DocumentData),
    );
    await batch.commit();
  },
};

/**
 * The active store. Firestore in production (config present + flag on),
 * localStorage everywhere else so dev "just works".
 */
export const db: Store = useFirestore ? firestoreDb : localDb;

/** Generate a uuid v4. Cheap, no dependency. */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Current ISO-8601 UTC timestamp; matches what Firestore will emit. */
export const nowIso = () => new Date().toISOString();
